import React from 'react';
import PageHeader from '../components/PageHeader';
import './MethodologyPage.css';

interface Step {
  number: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  icon: React.ReactNode;
}

const steps: Step[] = [
  {
    number: '01',
    title: 'الاستكشاف والفهم',
    shortDescription:
      'أبدأ بالاستماع إليك بعمق وفهم متطلباتك وأهدافك الفعلية. أطرح الأسئلة الصحيحة لضمان فهم المشكلة الحقيقية قبل أي تفكير في الحل التقني.',
    longDescription:
      'في هذه المرحلة أجري جلسة استكشافية مفصّلة معك لفهم طبيعة عملك، جمهورك المستهدف، تحدياتك الحالية، وأهدافك على المدى البعيد. الهدف ليس فقط سماع "ماذا تريد" بل فهم "لماذا تريده" — لأن الحل الصحيح يبدأ بالسؤال الصحيح.',
    icon: (
      <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="6" stroke="#0369A1" strokeWidth="1.83" />
        <path d="M15 15L19 19" stroke="#0369A1" strokeWidth="1.83" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'التصميم والتخطيط',
    shortDescription:
      'أرسم خارطة طريق تقنية واضحة، أختار التقنيات المناسبة لطبيعة المشروع، وأصمم هيكل النظام لضمان قابلية التوسع والصيانة على المدى البعيد.',
    longDescription:
      'بناءً على الفهم العميق للمشروع، أضع مخططاً تفصيلياً يشمل: هيكل قاعدة البيانات، واجهات API، مكونات الواجهة، وتدفق البيانات. أختار التقنيات بناءً على متطلبات المشروع لا على ما هو "رائج" — كل قرار له مبرر واضح.',
    icon: (
      <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="2.75" y="2.75" width="6.42" height="6.42" rx="1.5" stroke="#0369A1" strokeWidth="1.83" />
        <rect x="12.83" y="2.75" width="6.42" height="6.42" rx="1.5" stroke="#0369A1" strokeWidth="1.83" />
        <rect x="2.75" y="12.83" width="6.42" height="6.42" rx="1.5" stroke="#0369A1" strokeWidth="1.83" />
        <rect x="12.83" y="12.83" width="6.42" height="6.42" rx="1.5" stroke="#0369A1" strokeWidth="1.83" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'التطوير والبناء',
    shortDescription:
      'أكتب كوداً نظيفاً ومنظماً يتبع أفضل الممارسات الصناعية. أطور بشكل تدريجي مع تواصل مستمر للحصول على تغذية راجعة وضمان التوافق مع رؤيتك.',
    longDescription:
      'أعمل بمنهجية Agile — أسلّمك نسخاً قابلة للاختبار على دفعات بدلاً من الاختفاء ثم التسليم النهائي. الكود مكتوب بمعايير صناعية: TypeScript للأمان، Clean Architecture للمرونة، وتوثيق واضح لكل شيء.',
    icon: (
      <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M7 6L2 11L7 16" stroke="#0369A1" strokeWidth="1.83" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 6L20 11L15 16" stroke="#0369A1" strokeWidth="1.83" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13 4L9 18" stroke="#0369A1" strokeWidth="1.83" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'الاختبار والمراجعة',
    shortDescription:
      'أختبر كل جزء من النظام بدقة لضمان الجودة والأداء العالي. أراجع الكود وأتحقق من أن كل شيء يعمل بكفاءة وأمان قبل أي تسليم.',
    longDescription:
      'اختبارات شاملة تشمل: Unit Tests، Integration Tests، اختبارات الأداء تحت الضغط، واختبارات أمان الـ API. كذلك مراجعة UX/UI للتأكد من أن التجربة سلسة على جميع الأجهزة والمتصفحات.',
    icon: (
      <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <rect x="3" y="2" width="16" height="18" rx="2" stroke="#0369A1" strokeWidth="1.83" />
        <path d="M7 11L10 14L15 8" stroke="#0369A1" strokeWidth="1.83" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    number: '05',
    title: 'الإطلاق والدعم',
    shortDescription:
      'أطلق المشروع بنجاح وأقدم دعماً كاملاً بعد الإطلاق. أتابع الأداء وأستجيب لأي مشكلة بسرعة لضمان نجاح المشروع واستمراريته.',
    longDescription:
      'الإطلاق ليس نهاية العمل — بل بداية مرحلة جديدة. أقدم دعماً بعد الإطلاق يشمل: مراقبة الأداء، إصلاح أي أخطاء طارئة، وتقديم تقارير دورية. كذلك أوثّق كل شيء حتى تتمكن من إدارة مشروعك باستقلالية.',
    icon: (
      <svg viewBox="0 0 22 22" fill="none" aria-hidden="true">
        <path d="M14 2L20 8M20 8L14 14M20 8H10C7 8 5 10 5 13V20" stroke="#0369A1" strokeWidth="1.83" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const principles = [
  { title: 'التواصل المستمر', description: 'لا تفاجآت — أحدّثك بكل تطور بشكل منتظم وشفاف.' },
  { title: 'الجودة لا التسرع', description: 'أفضّل التأخير قليلاً على تسليم منتج ناقص أو هش.' },
  { title: 'الوضوح أولاً', description: 'لا أبدأ بالتطوير حتى تكون المتطلبات واضحة 100% للطرفين.' },
  { title: 'الكود كأصل', description: 'الكود الذي أكتبه يبقى قابلاً للفهم والتوسعة لسنوات.' },
];

const MethodologyPage: React.FC = () => {
  return (
    <section className="methodology-page">
      <div className="methodology-page-inner">
        <PageHeader
          title="منهجية العمل"
          subtitle="لا أؤمن بالحلول السريعة أو القوالب الجاهزة. كل مشروع له طبيعته، وهذا النهج يضمن أن نصل معاً إلى النتيجة الصحيحة."
          icon={
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 4L6 7L13 2M3 9L6 12L13 7" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
        />

        <div className="methodology-steps">
          {steps.map((step) => (
            <article key={step.number} className="methodology-step-card">
              <div className="methodology-step-content">
                <div className="methodology-step-icon">{step.icon}</div>
                <div className="methodology-step-body">
                  <div className="methodology-step-title-row">
                    <h2 className="methodology-step-title">{step.title}</h2>
                    <span className="methodology-step-number">{step.number}</span>
                  </div>
                  <p className="methodology-step-desc">{step.shortDescription}</p>
                  <p className="methodology-step-long">{step.longDescription}</p>
                </div>
              </div>
              <span className="methodology-step-watermark" aria-hidden="true">
                {step.number}
              </span>
              <div className="methodology-step-divider" />
            </article>
          ))}
        </div>

        <section className="methodology-principles">
          <h2 className="methodology-principles-title">مبادئ أعمل بها دائماً</h2>
          <div className="methodology-principles-grid">
            {principles.map((p) => (
              <article key={p.title} className="principle-card">
                <span className="principle-dot" aria-hidden="true" />
                <h3 className="principle-title">{p.title}</h3>
                <p className="principle-description">{p.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="methodology-cta">
          <h2 className="methodology-cta-title">هل مشروعك يحتاج هذا النهج؟</h2>
          <p className="methodology-cta-subtitle">تحدث معي وسنبني شيئاً تفخر به.</p>
          <a href="#contact" className="methodology-cta-button">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="white" strokeWidth="1.33" />
              <path d="M3 5L8 9L13 5" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>تواصل معي</span>
          </a>
        </section>
      </div>
    </section>
  );
};

export default MethodologyPage;
